import type { ReactNode } from 'react';
import type { Activity } from '../../../types';

interface ActivityWidgetProps {
  activities: Activity[];
  loading?: boolean;
}

const activityTypeIcons: Record<string, string> = {
  call: '📞',
  email: '✉️',
  meeting: '📅',
  task: '✅',
  note: '📝',
};

function formatDueDate(dueDate: string, dueTime?: string): string {
  const date = new Date(dueDate);
  const now = new Date();
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const isToday = date.toDateString() === now.toDateString();
  const isTomorrow = date.toDateString() === tomorrow.toDateString();

  let dateStr: string;
  if (isToday) {
    dateStr = 'Today';
  } else if (isTomorrow) {
    dateStr = 'Tomorrow';
  } else {
    dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  if (dueTime) {
    return `${dateStr} at ${dueTime}`;
  }
  return dateStr;
}

export function ActivityWidget({ activities, loading }: ActivityWidgetProps): ReactNode {
  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">Upcoming Activities</h3>
        <a href="/activities" className="text-sm text-ds-primary hover:text-ds-primary/80">
          View all →
        </a>
      </div>

      {loading ? (
        <div className="py-8 text-center text-gray-500">
          Loading activities...
        </div>
      ) : activities.length === 0 ? (
        <div className="py-8 text-center text-gray-500">
          <p>No upcoming activities</p>
        </div>
      ) : (
        <div className="space-y-3">
          {activities.map((activity) => (
            <div
              key={activity.id}
              className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <span className="text-xl" role="img" aria-label={activity.type}>
                {activityTypeIcons[activity.type] || '📌'}
              </span>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 truncate">
                  {activity.subject}
                </p>
                <p className="text-sm text-gray-500">
                  {activity.due_date && formatDueDate(activity.due_date, activity.due_time)}
                </p>
              </div>
              <span
                className={`px-2 py-1 text-xs rounded-full ${
                  activity.type === 'call'
                    ? 'bg-blue-100 text-blue-700'
                    : activity.type === 'meeting'
                    ? 'bg-purple-100 text-purple-700'
                    : activity.type === 'email'
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-100 text-gray-700'
                }`}
              >
                {activity.type}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
